import { defineBoot } from '#q-app';
import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { Router } from 'vue-router';
import { Notify } from 'quasar';
import {
  installCorrelationIdInterceptor,
  installClientIdentityHeaders,
  installMaintenanceModeLogger,
  CORRELATION_ID_CONFIG_KEY,
  SUPPRESS_ERROR_TOAST_CONFIG_KEY,
  deriveErrorRef,
} from '@shared/core/api';
import { useOAuthStore, clearUserScopedState } from 'src/stores/oauth';
import { useUserStore } from 'src/stores/user';
import { useI18nStore } from 'src/stores/i18n';
import { useAppStore } from 'src/stores/app';
import { websocketService } from 'src/services/websocket';
import { devLog, devError, devWarn } from '@shared/vue/utils/debug';

// Router reference captured in defineBoot() so the 401 handler below (module
// scope, runs before/outside any component) can SPA-navigate to /login
// instead of forcing a hard reload — mirrors apps/ui/src/boot/api.ts.
let appRouter: Router | null = null;

/** Router accessor for module-scope code outside defineBoot() (stores/oauth.ts onLogout). */
export function getAppRouter(): Router | null {
  return appRouter;
}

// ============================================================
// Token-refresh serialization
//
// Without this, a burst of N parallel requests after token expiry would
// each trigger their own /oauth/token round-trip — rotating the refresh
// token N-1 extra times and racing for the result. The first request to
// notice a stale token starts a refresh; the rest queue on the same
// promise and resume with the new bearer once it lands. Mirrors UI's
// pattern in apps/ui/src/boot/api.ts.
// ============================================================
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

// Dismiss handle for the currently-displayed "Unable to connect" toast.
// Held so a later successful response can clear it before it times out.
let activeNetworkErrorToastDismiss: (() => void) | null = null;

function clearNetworkErrorToast(): void {
  if (activeNetworkErrorToastDismiss) {
    activeNetworkErrorToastDismiss();
    activeNetworkErrorToastDismiss = null;
  }
}

const NETWORK_RETRY_DELAY_MS = 1000;
const NETWORK_RETRY_MAX = 1;

// Guards against multiple parallel 401s each spawning a toast + redirect.
let isAuthFailureInFlight = false;

// Debounce for the permission re-sync triggered by a policy 403 (see
// resyncStaffPermissions). A burst of forbidden responses must not spawn a
// refetch storm against /account/personal.
let lastPermissionResyncAt = 0;
const PERMISSION_RESYNC_DEBOUNCE_MS = 10_000;

function processQueue(token: string | null, error?: Error): void {
  refreshQueue.forEach((entry) => {
    if (error) {
      entry.reject(error);
    } else {
      entry.resolve(token);
    }
  });
  refreshQueue = [];
}

/**
 * Serialized access-token refresh.
 *
 * Calls `oauthStore.refreshAccessToken()` (NOT `restoreSession()` — that
 * one is for app-boot and does extra session-marker + CSRF fetch work
 * that's wasted in an inflight 401 path). Returns the new access token
 * or null on failure; on failure also wipes the in-memory token so
 * downstream code can detect anon state cheaply.
 */
async function attemptTokenRefresh(
  oauthStore: ReturnType<typeof useOAuthStore>,
): Promise<string | null> {
  if (isRefreshing) {
    return new Promise<string | null>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const tokenData = await oauthStore.refreshAccessToken();
    const accessToken = tokenData?.accessToken ?? null;
    processQueue(accessToken);
    return accessToken;
  } catch (refreshError) {
    devError('[AUTH] Token refresh failed:', refreshError);
    oauthStore.clearToken();
    processQueue(
      null,
      refreshError instanceof Error ? refreshError : new Error('Token refresh failed'),
    );
    return null;
  } finally {
    isRefreshing = false;
  }
}

// Create the main API axios instance
//
// HEADERS NOTE: do NOT add custom X-EMALINO-* headers here without first
// adding them to `api/config/cors.php` allowed_headers. The browser's CORS
// preflight check compares request headers against `access-control-allow-headers`;
// any unlisted custom header silently kills the request before it leaves the
// browser (the preflight succeeds with 204, but the actual request never fires
// — fetchUser-style flows then fail and the user is bounced back to login).
//
// Removed 2026-05-11:
//   'X-EMALINO-Client': 'staff'   — was telemetry only, never read server-side
//   'X-EMALINO-Version': '1.0.0'  — was telemetry only, never read server-side
// Both were in this file but absent from the API CORS allowlist, so EVERY
// authenticated SI request was failing at the preflight gate, including
// fetchUser('/account/personal') immediately after login. The result looked
// like "login impossible" because LoginPage logs out when fetchUser fails.
// (Client identity headers returned 2026-06-12 via the shared
// installClientIdentityHeaders — X-EMALINO-Client-{Version,Build,Git-Hash,
// Timezone} are server-read audit fields AND in the CORS allowlist.)
//
// The SI<->UI distinction the server actually uses is `X-EMALINO-App-Context`
// (set by the OAuth store factory's `appId: 'si'`), and that one IS in CORS.
// Read VITE_* env directly (not via `boot/config`) so values are
// compile-time substituted to literals at build time. Reading
// `config.api.X` here tripped an ESM cycle in the Vite 8 / rolldown
// chunked production build — `config` resolved to undefined when this
// top-level axios.create call ran. Same fix as `stores/oauth.ts`.
const api: AxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || ''}/v1`,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 50000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Send cookies for CSRF and refresh tokens
});

// Cross-app primitives: correlation ID + client identity + 503 logging.
// Bearer-token attachment is NOT delegated to the shared interceptor —
// the request interceptor below needs to be async (for pre-emptive
// refresh on stale tokens) and attaches the header itself.
installCorrelationIdInterceptor(api);
installClientIdentityHeaders(api);
installMaintenanceModeLogger(api, devWarn);

// Forward the staff member's selected language to the API so server-side
// strings (validation messages, mail subjects rendered server-side, etc.)
// come back in the same language as the SI shell. Mirrors UI's interceptor.
// `Accept-Language` is on the API CORS allowlist (it's a CORS-safelisted
// header — no preflight tweak needed).
api.interceptors.request.use((requestConfig) => {
  requestConfig.headers.set('Accept-Language', useI18nStore().currentLanguage);
  return requestConfig;
});

// ============================================================
// REQUEST INTERCEPTOR — pre-emptive refresh + token attach
//
// If the stored token is about to expire (or already has), we refresh
// BEFORE sending the request instead of letting it 401 and pay for the
// retry round-trip. Mirrors apps/ui/src/boot/api.ts:296-304.
//
// The token getter is called per-request — picks up rotations from the
// refresh flow (above) and from cross-tab BroadcastChannel sync.
// ============================================================
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const oauthStore = useOAuthStore();

    let tokenData = oauthStore.getToken;

    // Pre-emptive refresh: only attempt if we already have a token (no
    // point trying for anon requests) and the OAuth store says it's
    // expired. Skipped during deliberateLogout so a sign-out-in-flight
    // doesn't kick off a refresh race.
    if (tokenData && oauthStore.isTokenExpired && !oauthStore.deliberateLogout) {
      const refreshed = await attemptTokenRefresh(oauthStore);
      if (refreshed === null) {
        devWarn('[AUTH] Pre-request refresh failed; proceeding without token');
      }
      tokenData = oauthStore.getToken;
    }

    if (tokenData?.accessToken) {
      config.headers.set(
        'Authorization',
        `${tokenData.tokenType ?? 'Bearer'} ${tokenData.accessToken}`,
      );
    }

    // X-Socket-ID lets the server's broadcast()->toOthers() skip the originating
    // client, so a staff user never receives the realtime echo of their own write.
    // This is the Laravel framework header (BroadcastManager reads exactly
    // 'X-Socket-ID'); null until Echo connects, in which case it is simply omitted.
    const socketId = websocketService.getSocketId();
    if (socketId) {
      config.headers.set('X-Socket-ID', socketId);
    }

    return config;
  },
);

// ============================================================
// ERROR HELPERS — network, 503, auth failure
// ============================================================

function handleNetworkError(error: AxiosError): void {
  devError('[API] Network error:', {
    url: error.config?.url,
    method: error.config?.method,
    message: error.message,
  });

  // Suppress if the server is already known to be in maintenance — the
  // overlay handles the outage display; a second toast confuses staff.
  const appStore = useAppStore();
  if (appStore.isMaintenanceMode) return;

  const i18nStore = useI18nStore();
  const message = i18nStore.t('common.error.unableToConnectToServer');

  clearNetworkErrorToast();
  activeNetworkErrorToastDismiss = Notify.create({ type: 'negative', message });
}

/**
 * A policy 403 ('forbidden') can mean the staff member's server-side
 * permissions drifted (e.g. a production re-seed that re-synced the
 * superadmin's grants) AFTER the SI cached them — leaving permission-gated
 * action controls visible that 403 the moment they're used. Re-fetch the
 * profile so `userStore.system_permissions` reflects reality and the stale
 * controls disappear. Debounced, and never fired for the profile endpoint
 * itself (that would loop).
 */
function resyncStaffPermissions(failedUrl: string): void {
  if (failedUrl.endsWith('/account/personal')) return;
  const now = Date.now();
  if (now - lastPermissionResyncAt < PERMISSION_RESYNC_DEBOUNCE_MS) return;
  lastPermissionResyncAt = now;
  void useUserStore().fetchUser();
}

function handle503Error(error: AxiosError): void {
  const appStore = useAppStore();
  const errorData = error.response?.data as
    | { message?: string; error_type?: string }
    | undefined;

  const retryAfterHeader = error.response?.headers?.['retry-after'];
  const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;

  const rawType = errorData?.error_type;
  const errorType =
    rawType === 'deploy_in_progress' || rawType === 'maintenance_window' ? rawType : null;

  appStore.setMaintenanceMode(true, errorData?.message, retryAfter, errorType);
}

// ============================================================
// RESPONSE INTERCEPTOR — 401 refresh-then-retry, 403 surface, 503 maintenance,
// network-error retry + toast, fallthrough
// ============================================================
api.interceptors.response.use(
  (response) => {
    // Clear any stale "unable to connect" toast once a real response lands.
    clearNetworkErrorToast();

    // Detect partial maintenance on successful 2xx (soft banner, non-blocking).
    const maintenanceHeader = response.headers?.['x-emalino-maintenance-mode'];
    const appStore = useAppStore();
    if (maintenanceHeader && typeof maintenanceHeader === 'string') {
      if (!appStore.isMaintenanceMode) {
        appStore.setMaintenanceMode(true, undefined, undefined, 'maintenance_window');
      }
    } else if (
      appStore.isMaintenanceMode &&
      appStore.maintenanceErrorType === 'maintenance_window'
    ) {
      appStore.clearMaintenanceMode();
    }

    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // ----- Network error (no response from server) -----
    // status 0 / "Network Error" = request never reached the server, safe
    // to retry once (no server-side state mutated). On second failure, show
    // a self-dismissing toast that clears when any later request succeeds.
    if (!error.response) {
      const isNetworkLevelFailure =
        !!error.request && (error.request.status === 0 || error.message === 'Network Error');
      const retryConfig = error.config as
        | (InternalAxiosRequestConfig & { _networkRetryCount?: number })
        | undefined;

      if (
        isNetworkLevelFailure &&
        retryConfig &&
        (retryConfig._networkRetryCount ?? 0) < NETWORK_RETRY_MAX
      ) {
        retryConfig._networkRetryCount = (retryConfig._networkRetryCount ?? 0) + 1;
        devLog(
          `[API] Network-level failure on ${retryConfig.method?.toUpperCase() ?? 'REQ'} ${retryConfig.url} — retrying ${retryConfig._networkRetryCount}/${NETWORK_RETRY_MAX} after ${NETWORK_RETRY_DELAY_MS}ms`,
        );
        await new Promise((r) => setTimeout(r, NETWORK_RETRY_DELAY_MS));
        return api(retryConfig);
      }

      handleNetworkError(error);
      return Promise.reject(error);
    }

    // ----- 503 Service Unavailable (Maintenance Mode) -----
    if (status === 503) {
      handle503Error(error);
      return Promise.reject(error);
    }

    // ----- 401 Unauthorized: refresh-then-retry once, then redirect to login -----
    // Mirrors apps/ui/src/boot/api.ts `handle401Error`: EVERY 401 is handled,
    // not just the first. A request that 401s AGAIN after a refresh-and-retry
    // (config._retry already set — e.g. the refreshed token is still rejected,
    // or the staff session was revoked server-side) must still tear down auth
    // and bounce to /login, NOT fall through and silently reject. The previous
    // `&& !config?._retry` guard skipped the retried 401 entirely, leaving the
    // user stuck on the page with no redirect — the divergence from UI.
    if (status === 401) {
      // Fire-and-forget telemetry: a 401 here means the user is anon
      // (not logged in yet) hitting a public surface. Logging them out
      // on telemetry would be hostile. Mirrors UI's guard.
      const url = config?.url ?? '';
      // Skip auth-failure handling for: anon telemetry (logging an anon user
      // out on a public 401 would be hostile), and the best-effort clear-cookies
      // POST that handleAuthFailure itself fires — a 401 there must not re-enter
      // handleAuthFailure and recurse once the dedup flag has been released.
      if (url.endsWith('/v1/debug-log') || url.endsWith('/auth/si/clear-cookies')) {
        return Promise.reject(error);
      }

      const oauthStore = useOAuthStore();

      // First 401 on this request: attempt a single refresh-then-retry.
      if (config && !config._retry && !oauthStore.deliberateLogout) {
        config._retry = true;

        const newToken = await attemptTokenRefresh(oauthStore);

        if (newToken && !oauthStore.isTokenExpired) {
          config.headers.set('Authorization', `Bearer ${newToken}`);
          return api.request(config);
        }
      }

      // Retry exhausted, refresh failed, or a logout is in flight: tear down
      // auth state and bounce to login. Do NOT await the redirect — we still
      // want the original promise to reject so callers' .catch branches run
      // synchronously.
      const errorBody = error.response?.data as
        | { error_type?: string; error?: string; message?: string; error_description?: string }
        | undefined;
      handleAuthFailure(
        errorBody?.error_type || errorBody?.error,
        errorBody?.message || errorBody?.error_description,
      );

      return Promise.reject(error);
    }

    // ----- 403 Forbidden: surface required_permission + error ref for triage -----
    // The server attaches `required_permission` on policy failures and a `ref`
    // on all error envelopes so staff can quote the exact slug / reference to
    // support. Mirrors UI's handleApiError 403 branch.
    if (status === 403) {
      const errorBody = error.response?.data as
        | { message?: string; required_permission?: string; ref?: string; error_type?: string }
        | undefined;

      // Staff-suspended account: the API returns this on every protected route
      // (not just /broadcasting/auth). Force a clean logout + redirect with a
      // localized message instead of an opaque permission toast. Mirrors apps/ui.
      if (errorBody?.error_type === 'account_suspended') {
        handleAuthFailure('account_suspended');
        return Promise.reject(error);
      }

      // Policy denial: the staff member's cached permissions may be stale
      // (changed server-side after login). Re-validate so the SI stops
      // offering the forbidden action instead of re-firing into the 403.
      if (errorBody?.error_type === 'forbidden') {
        resyncStaffPermissions(config?.url ?? '');
      }

      const requiredPermission = errorBody?.required_permission;
      const message = errorBody?.message;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const correlationId = (config as any)?.[CORRELATION_ID_CONFIG_KEY] as string | undefined;
      const errorRef = errorBody?.ref ?? deriveErrorRef(correlationId);

      // Per-request opt-out (shared contract with UI): the caller fully owns
      // failure display. Suppresses only this generic toast — SI's only
      // generic display site — while the suspended logout and permission
      // resync above still ran. A new generic toast elsewhere in this
      // interceptor must repeat this guard (see the shared key's doc).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((config as any)?.[SUPPRESS_ERROR_TOAST_CONFIG_KEY]) {
        return Promise.reject(error);
      }

      if (message) {
        const i18nStore = useI18nStore();
        const notifyOpts: Record<string, unknown> = { type: 'negative', message };
        if (requiredPermission) {
          notifyOpts.caption = errorRef
            ? i18nStore.t('common.error.requiredPermissionWithRef', { slug: requiredPermission, ref: errorRef })
            : i18nStore.t('common.error.requiredPermission', { slug: requiredPermission });
        } else if (errorRef) {
          notifyOpts.caption = i18nStore.t('common.error.errorReference', { ref: errorRef });
        }
        Notify.create(notifyOpts);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Map OAuth error_type / error from the API response body to a localized
 * toast message. Falls through to the server-supplied message and then
 * to a generic "auth error" string. Mirrors UI's `getAuthErrorMessage`.
 */
function getAuthErrorMessage(
  errorType: string | undefined,
  errorMessage: string | undefined,
  i18nStore: ReturnType<typeof useI18nStore>,
): string {
  const map: Record<string, string> = {
    invalid_credentials: i18nStore.t('common.auth.invalidCredentials'),
    invalid_grant: i18nStore.t('common.auth.invalidGrant'),
    invalid_token: i18nStore.t('common.auth.sessionExpired'),
    expired_token: i18nStore.t('common.auth.sessionExpired'),
    access_denied: i18nStore.t('common.auth.accessDenied'),
    account_suspended: i18nStore.t('common.auth.accountSuspended'),
    csrf_mismatch: i18nStore.t('common.auth.csrfMismatch'),
    authentication_required: i18nStore.t('common.auth.sessionExpired'),
  };
  return (
    map[errorType ?? ''] ?? errorMessage ?? i18nStore.t('common.auth.authError')
  );
}

/**
 * Clear auth state, notify, and navigate to the login page.
 *
 * Idempotent — if the user is already on the login route (route name
 * 'login', which sits at `/`), the redirect AND the notify are skipped
 * to avoid a loop and a double-toast when a stale in-flight request
 * 401s after the user has already been bounced. A module-level
 * `isAuthFailureInFlight` guard also dedupes parallel-401 bursts that
 * each call here before the router.replace completes.
 */
function handleAuthFailure(errorType?: string, errorMessage?: string): void {
  if (isAuthFailureInFlight) return;
  isAuthFailureInFlight = true;
  // Backstop: the flag is normally cleared when the redirect navigation settles
  // (.finally below), but a cancelled/aborted navigation would never settle and
  // leave the flag stuck true — silently swallowing every subsequent auth
  // failure for the rest of the session. Always release it after a few seconds.
  setTimeout(() => {
    isAuthFailureInFlight = false;
  }, 5000);

  const oauthStore = useOAuthStore();
  const i18nStore = useI18nStore();

  // Clears userStore.data plus every other per-staff store (notifications,
  // tickets, support chat, acquisition, features) — this path never runs
  // the oauth store's onLogout hook, so it must tear down state itself.
  clearUserScopedState();
  oauthStore.clearToken();

  // Best-effort: wipe the server-side HttpOnly refresh cookie so the next
  // login starts clean. Fire-and-forget — a failure here must not block
  // or delay the local auth teardown + redirect.
  void api
    .post(`${import.meta.env.VITE_API_URL || ''}/auth/si/clear-cookies`)
    .catch(() => { /* best-effort */ });

  devLog('[AUTH] 401 — clearing session, redirecting to login', {
    errorType,
    errorMessage,
  });

  const alreadyOnLogin = appRouter?.currentRoute.value.name === 'login';
  if (alreadyOnLogin) {
    isAuthFailureInFlight = false;
    return;
  }

  Notify.create({
    type: 'negative',
    message: getAuthErrorMessage(errorType, errorMessage, i18nStore),
  });

  // Preserve the deep link as ?redirect= so LoginPage can return the
  // user to where they were after they sign back in. Same pattern as
  // UI's router beforeEach guard (apps/ui/src/router/index.ts:51-56).
  const currentFullPath = appRouter?.currentRoute.value.fullPath;
  const redirect =
    currentFullPath && currentFullPath !== '/' && !currentFullPath.startsWith('/?')
      ? { name: 'login' as const, query: { redirect: currentFullPath } }
      : { name: 'login' as const };

  if (appRouter) {
    void appRouter.replace(redirect).finally(() => {
      isAuthFailureInFlight = false;
    });
  } else {
    // Router not booted yet — extremely rare (interceptor fires during
    // early boot). Hard reload so the public LoginPage owns the redirect.
    isAuthFailureInFlight = false;
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  }
}

export default defineBoot(({ app, router }) => {
  // Capture router for the module-level 401 handler above.
  appRouter = router;

  // Make api available globally
  app.config.globalProperties.$api = api;
  app.provide('api', api);
});

// Export for direct imports
export { api };
